import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Leaf } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hey! I'm the Mudbutt Brew assistant. Ask me anything - which tea is right for you, herbal questions, brewing tips, or bundle suggestions. What can I help with?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId })
      });
      const data = await res.json();
      if (data.response) {
        setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
        if (data.session_id) setSessionId(data.session_id);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: "Sorry, I'm having trouble connecting. Try again in a moment!" }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Quick action buttons
  const quickActions = [
    "Find my perfect tea",
    "What helps with sleep?",
    "Recommend a bundle",
    "Tell me about your herbs"
  ];

  return (
    <>
      {/* Chat Bubble Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
          style={{ backgroundColor: '#3a692b', border: '2px solid #c2a357', boxShadow: '0 4px 15px rgba(58, 105, 43, 0.4)' }}
          data-testid="chat-bubble-btn"
        >
          <MessageCircle className="w-6 h-6" style={{ color: '#ffffff' }} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-20 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ height: '520px', border: '2px solid #c2a357' }}
          data-testid="chat-window"
        >
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center justify-between shrink-0"
            style={{ backgroundColor: '#5e3a1c' }}
          >
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5" style={{ color: '#c2a357' }} />
              <div>
                <p className="font-bold text-sm" style={{ color: '#c2a357' }}>Brew Assistant</p>
                <p className="text-xs" style={{ color: '#f5e9d0', opacity: 0.8 }}>Ask me anything about our teas</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
              data-testid="chat-close-btn"
            >
              <X className="w-5 h-5" style={{ color: '#f5e9d0' }} />
            </button>
          </div>

          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
            style={{ backgroundColor: '#faf6ee' }}
          >
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed"
                  style={msg.role === 'user'
                    ? { backgroundColor: '#5e3a1c', color: '#f5e9d0', borderBottomRightRadius: '4px' }
                    : { backgroundColor: '#ffffff', color: '#5e3a1c', border: '1px solid #e0d5c0', borderBottomLeftRadius: '4px' }
                  }
                  data-testid={`chat-msg-${i}`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Quick Actions - only show at start */}
            {messages.length === 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-1">
                {quickActions.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInput(action);
                      setTimeout(() => {
                        setInput('');
                        setMessages(prev => [...prev, { role: 'user', text: action }]);
                        setLoading(true);
                        fetch(`${API_URL}/api/chat`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ message: action, session_id: sessionId })
                        })
                          .then(r => r.json())
                          .then(data => {
                            if (data.response) {
                              setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
                              if (data.session_id) setSessionId(data.session_id);
                            }
                            setLoading(false);
                          })
                          .catch(() => {
                            setMessages(prev => [...prev, { role: 'assistant', text: "Sorry, try again!" }]);
                            setLoading(false);
                          });
                      }, 0);
                    }}
                    className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105"
                    style={{ backgroundColor: '#f5e9d0', color: '#5e3a1c', border: '1px solid #c2a357' }}
                    data-testid={`quick-action-${i}`}
                  >
                    {action}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex justify-start">
                <div
                  className="px-4 py-2 rounded-2xl text-sm"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e0d5c0' }}
                >
                  <span className="inline-flex gap-1">
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#c2a357', animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#c2a357', animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#c2a357', animationDelay: '300ms' }}></span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div
            className="px-3 py-2 flex items-center gap-2 shrink-0"
            style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e0d5c0' }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about our teas..."
              className="flex-1 px-3 py-2 rounded-full text-sm outline-none"
              style={{ backgroundColor: '#faf6ee', border: '1px solid #c2a357', color: '#5e3a1c' }}
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-all"
              style={{
                backgroundColor: input.trim() ? '#5e3a1c' : '#d4c5a9',
                cursor: input.trim() ? 'pointer' : 'default'
              }}
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" style={{ color: '#f5e9d0' }} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
